import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import {
  ArrowLeft, Sparkles, Send, Square, Copy, Check, RotateCcw,
  Instagram, Facebook, Linkedin, Twitter, Youtube, MessageCircle,
  CalendarDays, Hash, Clapperboard, Megaphone, Star, HelpCircle,
  Image as ImageIcon, Presentation, ChevronDown, ExternalLink, Wand2,
  ShieldCheck, AlertTriangle, Download, Loader2, Layers,
} from 'lucide-react';
import { API, getUserPhone } from '../lib/api';

const LANGUAGES = [
  'English', 'Hindi', 'Hinglish', 'Bengali', 'Marathi', 'Telugu',
  'Tamil', 'Gujarati', 'Kannada', 'Malayalam', 'Punjabi', 'Odia',
  'Assamese', 'Urdu', 'Bhojpuri', 'Konkani', 'Nepali', 'Sanskrit',
];

// Third-party creative tools. We can't legally pre-fill their editors via URL
// params, so we copy the relevant brief/outline to the clipboard and open the
// tool in a new tab for the doctor to paste.
const PRESENTATION_TOOLS = [
  { id: 'gamma', label: 'Gamma', url: 'https://gamma.app/create', hint: 'Paste your outline into “Create with AI”.' },
  { id: 'presentations-ai', label: 'Presentations.AI', url: 'https://www.presentations.ai/', hint: 'Paste your outline to auto-build slides.' },
];

// Client-side PHI guard (inspired by ZEMY's PHI scanner). Flags likely
// patient-identifiable info in the topic before anything is generated.
const PHI_PATTERNS = [
  { label: 'phone number', re: /\b(?:\+?91[-\s]?)?[6-9]\d{9}\b/ },
  { label: 'email address', re: /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/ },
  { label: 'a date of birth / date', re: /\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/ },
  { label: 'a patient ID / MRN', re: /\b(?:mrn|uhid|reg(?:istration)?\s*no|patient\s*id)\b/i },
  { label: 'Aadhaar number', re: /\b\d{4}\s?\d{4}\s?\d{4}\b/ },
  { label: 'a named patient ("Mrs./Mr. ...")', re: /\b(?:mrs?|ms|miss|smt)\.?\s+[A-Z][a-z]+/ },
];
const scanPHI = (text) => PHI_PATTERNS.filter((p) => p.re.test(text)).map((p) => p.label);

const PLATFORMS = [
  { id: 'Instagram', icon: Instagram },
  { id: 'WhatsApp', icon: MessageCircle },
  { id: 'Facebook', icon: Facebook },
  { id: 'LinkedIn', icon: Linkedin },
  { id: 'Twitter / X', icon: Twitter },
  { id: 'YouTube Shorts', icon: Youtube },
];

const POST_TYPES = [
  { id: 'Educational', desc: 'Myth vs fact · 3 things to know' },
  { id: 'Patient-Friendly', desc: 'Reassuring, jargon-free' },
  { id: 'Awareness Day', desc: 'PCOS · Menopause · Cancer day' },
  { id: 'Promotional', desc: 'Service / package — NMC-compliant' },
  { id: 'Q&A', desc: 'Top OPD questions answered' },
  { id: 'Success Story', desc: 'Anonymised testimonial' },
  { id: 'Inspirational', desc: 'Doctor reflection · story' },
  { id: 'New Launch', desc: 'Clinic · service · location' },
];

const IDEA_CHIPS = [
  'PCOS lifestyle changes that actually work',
  'When to get your first Pap smear',
  '5 pregnancy myths every patient believes',
  'Perimenopause signs women ignore',
  'IVF success rates — honest talk',
  'Why annual gynae check-ups matter',
];

const AWARENESS_DAYS = [
  { day: 'Jan', name: 'Cervical Cancer Awareness Month' },
  { day: '8 Mar', name: "International Women's Day" },
  { day: '11 Apr', name: 'National Safe Motherhood Day' },
  { day: '28 May', name: 'Menstrual Hygiene Day' },
  { day: '25 Jul', name: 'World IVF Day' },
  { day: '1–7 Aug', name: 'World Breastfeeding Week' },
  { day: 'Sep', name: 'PCOS Awareness Month' },
  { day: '26 Sep', name: 'World Contraception Day' },
  { day: 'Oct', name: 'Breast Cancer Awareness Month' },
  { day: '18 Oct', name: 'World Menopause Day' },
];

const POWER_TOOLS = [
  {
    id: 'calendar', icon: CalendarDays, label: '30-Day Content Calendar',
    desc: 'A full month of post ideas, mapped day-by-day',
    prompt: (p, t) => `Social media for ${p} · Educational: Create a complete 30-day content calendar for a doctor's professional page (any speciality)${t ? ` themed around "${t}"` : ''}. Format it as a clean, premium REPORT using Markdown exactly in this structure:\n\n# 30-Day Content Calendar\nA one-line intro sentence about the month's focus.\n\n## Monthly Snapshot\n- **Goal:** ...\n- **Platform:** ${p}\n- **Posting cadence:** ... per week\n- **Content mix:** Educational / Awareness / Q&A / Patient-Friendly / Promotional\n\n## Week 1\nA proper Markdown table with these exact columns: | Day | Post Idea | Format | Caption Hook | Best Time |\nUse rows for days 1-7. 'Format' = Post / Reel / Story / Carousel.\n\n## Week 2\n(table, days 8-14)\n\n## Week 3\n(table, days 15-21)\n\n## Week 4\n(table, days 22-28)\n\n## Week 5\n(table, days 29-30)\n\n## Weekly Themes\n- **Week 1:** theme\n- **Week 2:** theme ... (etc.)\n\n## Pro Posting Tips\n3-5 concise bullet tips.\n\nKeep every table row concise (caption hook ≤ 8 words). Always use valid Markdown table syntax with header separators.`,
  },
  {
    id: 'hashtags', icon: Hash, label: 'Hashtag Pack',
    desc: '30 researched hashtags, grouped by reach',
    prompt: (p, t) => `Social media for ${p} · Educational: Build a hashtag pack of 30 hashtags for an Indian doctor's page${t ? ` about "${t}"` : ''} — grouped as High-reach (>500k), Mid-niche (50k–500k) and Local/branded, with a one-line strategy on how to rotate them.`,
  },
  {
    id: 'reel', icon: Clapperboard, label: 'Reel Script',
    desc: '30–45 sec script with hook, shots & CTA',
    prompt: (p, t) => `Social media for ${p} · Educational: Write a 30–45 second reel script ${t ? `on "${t}"` : 'on a high-engagement medical topic of your choice (any speciality)'} — include HOOK (first 2 sec), scene-by-scene shot list, on-screen text, voiceover lines and CTA.`,
  },
  {
    id: 'campaign', icon: Megaphone, label: 'Awareness Campaign',
    desc: '7-day multi-post campaign for a health week',
    prompt: (p, t) => `Social media for ${p} · Awareness Day: Design a 7-day awareness campaign ${t ? `for "${t}"` : "for the next upcoming women's health awareness day/month"} — one post per day with format, caption, hashtags and a finale CTA post.`,
  },
  {
    id: 'reviews', icon: Star, label: 'Review Replies',
    desc: 'Professional replies to Google/Practo reviews',
    prompt: (p, t) => `Social media for ${p} · Patient-Friendly: Write 6 professional, warm, NMC-compliant reply templates for a doctor's Google/Practo reviews${t ? ` (context: ${t})` : ''} — 3 for positive reviews, 2 for neutral, 1 for a negative review (empathetic, non-defensive, invites offline resolution).`,
  },
  {
    id: 'faq', icon: HelpCircle, label: 'FAQ Carousel',
    desc: 'Top-8 OPD questions as a swipe carousel',
    prompt: (p, t) => `Social media for ${p} · Q&A: Create an 8-slide FAQ carousel answering the most-asked OPD questions ${t ? `about "${t}"` : 'in a medical practice'} — slide-by-slide text (max 30 words/slide), cover slide hook, caption and hashtags.`,
  },
  {
    id: 'deck', icon: Presentation, label: 'Presentation Outline',
    desc: 'Slide-by-slide deck ready for Gamma / Presenti',
    prompt: (p, t) => `Social media for ${p} · Educational: Create a ready-to-build PRESENTATION OUTLINE for a patient-awareness talk by a doctor${t ? ` on "${t}"` : ''}. Output a clean slide-by-slide structure — for each slide give: "Slide N — <Title>" then 3-4 concise bullet points. Include 8-10 slides (title slide, agenda, key content slides, a myth-vs-fact slide, a Q&A slide, and a closing CTA slide with clinic contact placeholder). Keep it copy-paste ready for Gamma / Presentations.AI.`,
  },
];

const glass = 'bg-white/[0.04] backdrop-blur-2xl backdrop-saturate-150 border border-white/[0.08] rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]';

export default function GynovateSocialPage() {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState('Instagram');
  const [postType, setPostType] = useState('Educational');
  const [topic, setTopic] = useState('');
  const [language, setLanguage] = useState('English');
  const [output, setOutput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lastPrompt, setLastPrompt] = useState('');
  const [presentMenu, setPresentMenu] = useState(false);
  const [imageMenu, setImageMenu] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [imgUrl, setImgUrl] = useState('');
  const abortRef = useRef(null);
  const outputRef = useRef(null);
  const presentRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (presentRef.current && !presentRef.current.contains(e.target)) setPresentMenu(false);
      if (imageRef.current && !imageRef.current.contains(e.target)) setImageMenu(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const phiFlags = scanPHI(topic);

  useEffect(() => {
    if (streaming && outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output, streaming]);

  const runPrompt = async (query) => {
    if (streaming) return;
    setLastPrompt(query);
    setOutput('');
    setImgUrl('');
    setStreaming(true);
    setCopied(false);
    try {
      const sess = await axios.post(`${API}/api/sessions`, {
        mode: 'Social Media Manager',
        title: query.slice(0, 50),
        phone: getUserPhone(),
      });
      const controller = new AbortController();
      abortRef.current = controller;
      const res = await fetch(`${API}/api/sessions/${sess.data.id}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: query, mode: 'Social Media Manager', language }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = '';
      let sseBuf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuf += decoder.decode(value, { stream: true });
        const lines = sseBuf.split('\n');
        sseBuf = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.type === 'chunk') {
              full += parsed.content;
              setOutput(full);
            } else if (parsed.type === 'error') {
              throw new Error(parsed.message || 'AI response error');
            }
          } catch (e) {
            if (e.message && !e.message.includes('JSON')) throw e;
          }
        }
      }
    } catch (err) {
      if (err?.name === 'AbortError') toast.info('Generation stopped');
      else toast.error(err.message || 'Failed to generate');
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const handleGenerate = () => {
    if (!topic.trim()) { toast.error('Add a topic first'); return; }
    runPrompt(`Social media for ${platform} · ${postType}: ${topic.trim()}`);
  };

  const stopGen = () => { try { abortRef.current?.abort(); } catch { /* noop */ } };

  const copyOutput = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(output);
      } else {
        const ta = document.createElement('textarea');
        ta.value = output;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Copy failed'); }
  };

  const writeClipboard = async (text) => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      return true;
    } catch { return false; }
  };

  // Pull the IMAGE BRIEF section if present, else use the whole output as the visual prompt.
  const extractImageBrief = () => {
    const m = output.match(/(?:image\s*brief|visual\s*brief)[:\s]*([\s\S]*?)(?:\n#{1,3}\s|\n\*\*[A-Z]|$)/i);
    const brief = (m && m[1] ? m[1] : output).trim();
    return `Create a professional, NMC-compliant social media graphic for a doctor's ${platform} page. Style: clean, warm, trustworthy, no text overlay unless specified. Brief:\n${brief}`;
  };

  const createImageChatGPT = async () => {
    setImageMenu(false);
    const ok = await writeClipboard(extractImageBrief());
    toast.success(ok ? 'Image brief copied — paste it into ChatGPT' : 'Opening ChatGPT…');
    window.open('https://chatgpt.com/', '_blank', 'noopener,noreferrer');
  };

  const generateNanoImage = async () => {
    setImageMenu(false);
    setImgLoading(true);
    setImgUrl('');
    try {
      const res = await axios.post(`${API}/api/generate/image-nano`, { prompt: extractImageBrief() });
      if (res.data?.success && res.data.image_url) {
        setImgUrl(`${API}${res.data.image_url}`);
        toast.success('Image ready');
      } else {
        toast.error(res.data?.text || 'Could not generate image');
      }
    } catch {
      toast.error('Image generation failed');
    } finally {
      setImgLoading(false);
    }
  };

  const repurposeAll = async () => {
    const q = `Take the following medical social-media content and REPURPOSE it for every major platform. Keep the medical facts identical but adapt tone, length and format per platform. Output clearly separated sections: Instagram (caption + hashtags), WhatsApp broadcast (short, 1-2 emoji), Facebook, LinkedIn (professional), Twitter/X (under 280 chars), and YouTube Shorts (spoken script). Original content:\n\n${output}`;
    runPrompt(q);
  };

  const openPresentationTool = async (tool) => {
    setPresentMenu(false);
    const ok = await writeClipboard(output);
    toast.success(ok ? `Outline copied — ${tool.hint}` : `Opening ${tool.label}…`);
    window.open(tool.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden text-rose-50 bg-[#1A0B14]" data-testid="gynovate-social-page">
      {/* Layered ambient background */}
      <div className="pointer-events-none fixed inset-0" aria-hidden="true">
        <div
          className="absolute inset-0 opacity-20 mix-blend-screen bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1706785534889-9b40e60834fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')" }}
        />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at top right, rgba(136,19,55,0.4) 0%, rgba(26,11,20,0.3) 45%, #1A0B14 80%)' }} />
        <div className="absolute -top-32 -left-24 w-[560px] h-[560px] rounded-full opacity-20 blur-3xl" style={{ background: 'radial-gradient(circle, #c0123c 0%, transparent 65%)' }} />
        <div className="absolute bottom-0 right-0 w-[460px] h-[460px] rounded-full opacity-15 blur-3xl" style={{ background: 'radial-gradient(circle, #e8b08a 0%, transparent 65%)' }} />
        <div className="absolute inset-0 opacity-[0.07] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-6 z-10">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-10">
          <button
            onClick={() => navigate('/chat')}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
            data-testid="social-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Copilot
          </button>
          <div className="flex items-center gap-2.5">
            <img src="/gynovate-social-logo.png" alt="Gynovate Social" className="w-9 h-9" />
            <span className="font-accent text-lg tracking-wide text-white/90">Gynovate&nbsp;<span className="italic text-[#e8b08a] pr-1">Social</span></span>
          </div>
        </div>

        {/* Hero */}
        <div className="text-center mb-14 pt-6">
          <img src="/gynovate-social-logo.png" alt="Gynovate Social logo" className="w-24 h-24 mx-auto mb-6 drop-shadow-[0_10px_50px_rgba(192,18,60,0.5)]" data-testid="social-logo" />
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#e8b08a]/80 mb-4">The Creative Studio for Doctors</p>
          <h1 className="font-accent text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-5 leading-[1.12] text-white">
            Gynovate{' '}
            <span
              className="italic bg-clip-text text-transparent inline-block pr-2 font-medium"
              style={{ backgroundImage: 'linear-gradient(90deg, #f3b98e, #FB7185, #c0123c)' }}
            >
              Social
            </span>
          </h1>
          <p className="text-lg md:text-xl text-rose-50/70 max-w-2xl mx-auto leading-relaxed" data-testid="social-tagline">
            Your AI Social Media Manager for Doctors of every speciality — platform-native posts, reels,
            captions &amp; hashtags in the language your patients speak.
          </p>
          <div className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-full bg-black/40 backdrop-blur-md border border-white/10" data-testid="social-compliance-badge">
            <ShieldCheck className="w-4 h-4 text-emerald-300" strokeWidth={1.5} />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-white/70">Built-in PHI &amp; NMC / ASCI compliance guard</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Composer */}
          <div className={`lg:col-span-7 ${glass} p-6 sm:p-8`} data-testid="social-composer">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-5 h-5 text-[#e8b08a]" />
              <h2 className="text-lg md:text-xl font-bold tracking-wide">Compose a post</h2>
            </div>

            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/50 mb-2.5">Platform</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {PLATFORMS.map((p) => {
                const Icon = p.icon;
                const active = platform === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setPlatform(p.id)}
                    data-testid={`social-platform-${p.id.toLowerCase().replace(/[^a-z]/g, '-')}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold border transition-all ${
                      active
                        ? 'text-white border-transparent shadow-[0_4px_20px_rgba(192,18,60,0.4)]'
                        : 'bg-white/[0.04] text-rose-50/70 border-white/[0.08] hover:bg-white/10 hover:text-white'
                    } hover:-translate-y-[2px] transition-transform duration-200 focus-visible:ring-2 focus-visible:ring-rose-500/50`}
                    style={active ? { background: 'linear-gradient(135deg, #c0123c, #8a1030)' } : undefined}
                  >
                    <Icon className="w-4 h-4" /> {p.id}
                  </button>
                );
              })}
            </div>

            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/50 mb-2.5">Post type</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
              {POST_TYPES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setPostType(t.id)}
                  data-testid={`social-type-${t.id.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className={`text-left px-3 py-2.5 rounded-xl border transition-all ${
                    postType === t.id
                      ? 'bg-[#c0123c]/15 border-[#c0123c]/50 text-white'
                      : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                  }`}
                >
                  <div className="text-sm font-bold">{t.id}</div>
                  <div className="text-[11px] text-white/45 mt-0.5 leading-tight">{t.desc}</div>
                </button>
              ))}
            </div>

            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/50 mb-2.5">Topic</p>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleGenerate(); }}
              placeholder="e.g. PCOS lifestyle changes, World Menopause Day, IVF success rates"
              data-testid="social-topic-input"
              className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/15 text-base text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#c0123c]/60 focus:border-[#c0123c]/60 mb-3"
            />
            <div className="flex flex-wrap gap-1.5 mb-4">
              {IDEA_CHIPS.map((idea) => (
                <button
                  key={idea}
                  onClick={() => setTopic(idea)}
                  className="px-3 py-1.5 rounded-full text-xs text-white/60 bg-white/5 border border-white/10 hover:text-white hover:bg-white/10 transition-colors"
                  data-testid="social-idea-chip"
                >
                  {idea}
                </button>
              ))}
            </div>

            {phiFlags.length > 0 && (
              <div
                className="flex items-start gap-2.5 mb-4 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-400/30"
                data-testid="social-phi-warning"
              >
                <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-100/90 leading-relaxed">
                  <span className="font-bold">Privacy check:</span> your topic may contain patient-identifiable info
                  ({phiFlags.join(', ')}). Public posts must never include patient details — please remove it before generating.
                </p>
              </div>
            )}

            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/50 mb-2.5">Post language</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative shrink-0">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  data-testid="social-language-select"
                  className="w-full sm:w-48 appearance-none px-4 py-3.5 pr-10 rounded-xl bg-slate-900 border border-white/[0.12] text-base font-semibold text-white focus:outline-none focus:ring-2 focus:ring-[#c0123c]/60 focus:border-[#c0123c]/60 cursor-pointer [&>option]:bg-white [&>option]:text-slate-900"
                >
                  {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
                <ChevronDown className="w-4 h-4 text-[#e8b08a] absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
              {streaming ? (
                <button
                  onClick={stopGen}
                  data-testid="social-stop-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-white/10 border border-white/20 text-base font-bold hover:bg-white/15 transition-colors"
                >
                  <Square className="w-4 h-4 fill-current" /> Stop
                </button>
              ) : (
                <button
                  onClick={handleGenerate}
                  disabled={!topic.trim()}
                  data-testid="social-generate-btn"
                  className="relative overflow-hidden flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-xl text-base font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-opacity hover:opacity-95 shadow-[0_8px_30px_rgba(192,18,60,0.45)] focus-visible:ring-2 focus-visible:ring-rose-500/50 group"
                  style={{ background: 'linear-gradient(135deg, #c0123c, #7a0e2e)' }}
                >
                  <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent group-hover:translate-x-full transition-transform duration-700 ease-out pointer-events-none" />
                  <Send className="w-4 h-4 relative" strokeWidth={1.5} /> <span className="relative">Generate post</span>
                </button>
              )}
            </div>
          </div>

          {/* Right column: power tools + awareness days */}
          <div className="lg:col-span-5 space-y-6">
            <div className={`${glass} p-6`} data-testid="social-power-tools">
              <div className="mb-4">
                <h3 className="text-base font-bold tracking-wide text-white/90">One-tap studio</h3>
                <p className="text-[11px] text-white/45 mt-1 leading-snug">
                  {topic.trim() ? <>Using your topic: <span className="text-[#e8b08a] font-semibold">“{topic.trim()}”</span></> : 'Tip: type a topic above and these adapt to it.'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {POWER_TOOLS.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      onClick={() => runPrompt(t.prompt(platform, topic.trim()))}
                      disabled={streaming}
                      data-testid={`social-tool-${t.id}`}
                      className="text-left p-3.5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[#c0123c]/40 hover:-translate-y-[2px] hover:shadow-lg transition-transform duration-200 disabled:opacity-40 group focus-visible:ring-2 focus-visible:ring-rose-500/50"
                    >
                      <Icon className="w-5 h-5 text-[#e8b08a] mb-2 group-hover:scale-110 transition-transform" />
                      <div className="text-sm font-bold leading-tight">{t.label}</div>
                      <div className="text-[11px] text-white/45 mt-1 leading-tight">{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={`${glass} p-6`} data-testid="social-awareness-days">
              <h3 className="text-base font-bold tracking-wide mb-4 text-white/90">Awareness calendar</h3>
              <div className="space-y-1.5">
                {AWARENESS_DAYS.map((d) => (
                  <button
                    key={d.name}
                    onClick={() => { setPostType('Awareness Day'); setTopic(d.name); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors text-left group"
                    data-testid="social-awareness-item"
                  >
                    <span className="shrink-0 w-14 text-[11px] font-bold text-[#e8b08a] uppercase tracking-wider">{d.day}</span>
                    <span className="text-sm text-white/65 group-hover:text-white transition-colors">{d.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Output */}
        {(output || streaming) && (
          <div className={`${glass} mt-8 overflow-hidden animate-fade-in`} data-testid="social-output">
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Sparkles className={`w-5 h-5 text-[#e8b08a] ${streaming ? 'animate-pulse' : ''}`} />
                <span className="text-base font-bold">{streaming ? 'Crafting your post…' : 'Your post is ready'}</span>
              </div>
              {!streaming && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => runPrompt(lastPrompt)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                    data-testid="social-regenerate-btn"
                  >
                    <RotateCcw className="w-4 h-4" /> Regenerate
                  </button>
                  <button
                    onClick={repurposeAll}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                    data-testid="social-repurpose-btn"
                    title="Adapt this content for Instagram, WhatsApp, Facebook, LinkedIn, X & YouTube Shorts"
                  >
                    <Layers className="w-4 h-4 text-[#e8b08a]" /> Adapt for all platforms
                  </button>
                  <div className="relative" ref={imageRef}>
                    <button
                      onClick={() => setImageMenu((v) => !v)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                      data-testid="social-create-image-btn"
                      title="Create a matching visual for this post"
                    >
                      <ImageIcon className="w-4 h-4 text-[#e8b08a]" /> Create image
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${imageMenu ? 'rotate-180' : ''}`} />
                    </button>
                    {imageMenu && (
                      <div className="absolute right-0 mt-2 w-64 rounded-2xl border border-white/10 bg-[#22101c]/95 backdrop-blur-xl shadow-2xl z-20 overflow-hidden" data-testid="social-image-menu">
                        <button
                          onClick={generateNanoImage}
                          data-testid="social-image-nano"
                          className="w-full flex items-start gap-2.5 px-4 py-3 text-left hover:bg-white/10 transition-colors"
                        >
                          <Wand2 className="w-4 h-4 text-[#e8b08a] mt-0.5 shrink-0" />
                          <span>
                            <span className="block text-sm font-semibold text-white/90">Generate here · Nano Banana</span>
                            <span className="block text-[11px] text-white/45 mt-0.5">Instant AI image, right in Gynovate</span>
                          </span>
                        </button>
                        <button
                          onClick={createImageChatGPT}
                          data-testid="social-image-chatgpt"
                          className="w-full flex items-start gap-2.5 px-4 py-3 text-left hover:bg-white/10 transition-colors border-t border-white/10"
                        >
                          <ExternalLink className="w-4 h-4 text-white/50 mt-0.5 shrink-0" />
                          <span>
                            <span className="block text-sm font-semibold text-white/90">Open in ChatGPT</span>
                            <span className="block text-[11px] text-white/45 mt-0.5">Copies the brief &amp; opens ChatGPT</span>
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="relative" ref={presentRef}>
                    <button
                      onClick={() => setPresentMenu((v) => !v)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                      data-testid="social-presentation-btn"
                      title="Copy the outline and open a presentation builder"
                    >
                      <Presentation className="w-4 h-4 text-[#e8b08a]" /> Presentations
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${presentMenu ? 'rotate-180' : ''}`} />
                    </button>
                    {presentMenu && (
                      <div
                        className="absolute right-0 mt-2 w-56 rounded-2xl border border-white/10 bg-[#22101c]/95 backdrop-blur-xl shadow-2xl z-20 overflow-hidden"
                        data-testid="social-presentation-menu"
                      >
                        {PRESENTATION_TOOLS.map((t) => (
                          <button
                            key={t.id}
                            onClick={() => openPresentationTool(t)}
                            data-testid={`social-present-${t.id}`}
                            className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-white/10 transition-colors"
                          >
                            <span className="text-sm font-semibold text-white/90">{t.label}</span>
                            <ExternalLink className="w-3.5 h-3.5 text-white/40" />
                          </button>
                        ))}
                        <div className="px-4 py-2.5 border-t border-white/10 text-[11px] text-white/40 leading-snug">
                          Outline is copied to your clipboard — paste it into the tool.
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={copyOutput}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #c0123c, #7a0e2e)' }}
                    data-testid="social-copy-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}
            </div>
            <div ref={outputRef} className="px-6 sm:px-8 py-6 max-h-[65vh] overflow-y-auto">
              <div className="text-[15px] leading-[1.75] font-normal" style={{ fontFamily: "'Public Sans', system-ui, sans-serif" }}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: (props) => <h1 className="font-accent text-2xl sm:text-3xl font-semibold text-white mt-2 mb-4 tracking-tight" {...props} />,
                    h2: (props) => (
                      <h2 className="flex items-center gap-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#e8b08a] mt-7 mb-3 first:mt-0" {...props}>
                        <span className="inline-block w-6 h-[2px] rounded bg-[#c0123c]" />
                        <span>{props.children}</span>
                      </h2>
                    ),
                    h3: (props) => <h3 className="text-lg font-bold text-[#f0c3a5] mt-5 mb-2" {...props} />,
                    p: (props) => <p className="text-white/85 mb-4" {...props} />,
                    strong: (props) => <strong className="text-white font-bold" {...props} />,
                    em: (props) => <em className="text-white/70 italic" {...props} />,
                    ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-2 text-white/85 marker:text-[#c0123c]" {...props} />,
                    ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-2 text-white/85 marker:text-[#e8b08a]" {...props} />,
                    li: (props) => <li className="text-white/85 pl-1" {...props} />,
                    a: (props) => <a className="text-[#f0c3a5] underline underline-offset-2 hover:text-white" target="_blank" rel="noopener noreferrer" {...props} />,
                    table: (props) => <div className="overflow-x-auto mb-4 rounded-xl border border-white/10"><table className="w-full text-sm border-collapse" {...props} /></div>,
                    thead: (props) => <thead className="bg-white/[0.07]" {...props} />,
                    th: (props) => <th className="px-3.5 py-2.5 text-left font-bold text-[#f0c3a5] border-b border-white/10" {...props} />,
                    td: (props) => <td className="px-3.5 py-2.5 text-white/80 border-b border-white/5" {...props} />,
                    code: (props) => <code className="bg-[#c0123c]/20 rounded px-1.5 py-0.5 text-[#f3b98e] text-[13px] font-mono" {...props} />,
                    hr: () => <hr className="border-white/10 my-6" />,
                    blockquote: (props) => <blockquote className="border-l-2 border-[#c0123c] bg-white/[0.03] rounded-r-lg pl-4 pr-3 py-2 text-white/70 italic mb-4" {...props} />,
                  }}
                >
                  {output || '…'}
                </ReactMarkdown>
              </div>

              {(imgLoading || imgUrl) && (
                <div className="mt-6 pt-6 border-t border-white/10" data-testid="social-image-result">
                  <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#e8b08a] mb-3">
                    <span className="inline-block w-6 h-[2px] rounded bg-[#c0123c]" /> Generated visual
                  </p>
                  {imgLoading ? (
                    <div className="flex items-center gap-3 text-white/60 text-sm py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-[#e8b08a]" /> Painting your visual with Nano Banana…
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <img src={imgUrl} alt="Generated social visual" className="max-w-sm w-full rounded-2xl border border-white/10 shadow-2xl" data-testid="social-generated-image" />
                      <a
                        href={imgUrl}
                        download="gynovate-social-image.png"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                        data-testid="social-image-download"
                      >
                        <Download className="w-4 h-4" /> Download image
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <p className="text-center text-xs text-white/30 mt-10 pb-6">
          All content follows NMC &amp; ASCI healthcare advertising guidelines · Review before posting
        </p>
      </div>
    </div>
  );
}
